import React, { useState, useEffect } from "react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import {
  Calendar,
  Edit,
  Trash2,
  Mail,
  Phone,
  Lock,
  UserCheck,
  UserX,
  MapPin,
  XCircle,
  Clock,
  CheckCircle,
  Building2,
} from "lucide-react";
import LocalDateTime from "services/date-converter";
import Pagination from "modules/swiftboard/components/Pagination";
import sortData from "utils/sorting-coloum";
import { useAlert } from "context/AlertContext";
import Avatar from "components/ui/Avatar";
import NoDataState from "modules/swiftboard/components/NoDataState";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import apiUser from "../api";
import EntityFormModal from "modules/swiftboard/components/EntityFormModal";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import TradesCell from "modules/swiftboard/components/TradesCell";
import { loadClientDependencies } from "./features/client.dependencies";
import { clientFieldsConfig } from "./features/client.fields";
import { clientValidation } from "./features/client.validation";
import { clientFilterFields, loadClientFilterDependencies } from "./features/client.filter";

const SORT_OPTIONS = [
  { value: "created_date_desc", label: "Newest first" },
  { value: "created_date_asc", label: "Oldest first" },
];

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const EMPTY_FILTERS = {};

const Clients = ({ onTotalCountChange }) => {
  const { showAlert } = useAlert();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [confirmData, setConfirmData] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Yes",
    cancelText: "No",
    icon: null,
    resolve: null,
  });

  const [sortConfig, setSortConfig] = useState({
    key: "created_date",
    direction: "desc",
  });
  const [sortValue, setSortValue] = useState("created_date_desc");
  const [resetSelectionTrigger, setResetSelectionTrigger] = useState(0);
  const [exporting, setExporting] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const payload = {
        searchText,
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        role: "CLIENT",
        search: JSON.stringify(filters),
      };

      const response = await apiUser.getAllClients(payload);

      if (response.data.status) {
        setData(response.data.data);
        setTotalCount(response.data.total_count || 0);
        onTotalCountChange?.(response.data.total_count || 0);
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      if (error.response) {
        showAlert({ type: "error", message: error.response?.data?.message });
      } else {
        showAlert({
          type: "error",
          message: "An unexpected error occurred. Please try again!",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const openConfirm = (options) => {
    return new Promise((resolve) => {
      setConfirmData({
        ...options,
        isOpen: true,
        resolve,
      });
    });
  };

  const handleConfirmClose = (result) => {
    if (confirmData.resolve) {
      confirmData.resolve(result);
    }

    setConfirmData((prev) => ({
      ...prev,
      isOpen: false,
      resolve: null,
    }));
  };

  const handleDeleteSelected = async (ids = []) => {
    if (ids.length === 0) {
      showAlert({
        type: "warning",
        message: "Please select at least one country to delete.",
      });
      return;
    }
    const confirmed = await openConfirm({
      title: "Delete Client",
      message:
        ids.length > 1
          ? `Are you sure you want to delete ${ids.length} selected Clients? This action cannot be undone.`
          : "Are you sure you want to delete this Client? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      icon: <Trash2 size={24} className="text-danger" />,
    });

    if (!confirmed) return;
    try {
      const response = await apiUser.delete(ids);

      if (response.data.status) {
        showAlert({ type: "success", message: response.data?.message });

        fetchUsers();
        setSelectedRows([]);
        setResetSelectionTrigger((prev) => prev + 1);
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      if (error.response) {
        showAlert({ type: "error", message: error.response?.data?.message });
      } else {
        showAlert({
          type: "error",
          message: "An unexpected error occurred. Please try again!",
        });
      }
    }
  };

  const handleClearSelection = () => {
    setSelectedRows([]);
    setResetSelectionTrigger((prev) => prev + 1);
  };

  const handleSortChange = (value) => {
    setSortValue(value);
    setSortConfig({ key: "created_date", direction: value.endsWith("_desc") ? "desc" : "asc" });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await apiUser.getAllClients({
        searchText,
        skip: 0,
        limit: totalCount || 1000,
        role: "CLIENT",
        search: JSON.stringify(filters),
      });
      const rows = response.data?.data || [];

      if (rows.length === 0) {
        showAlert({ type: "warning", message: "There is no data to export." });
        return;
      }

      const header = ["Emp ID", "Name", "Email", "Phone", "Company", "Designation", "Business Type", "Status", "Last Login"];
      const lines = rows.map((row) =>
        [
          row.id,
          `${row.first_name || ""} ${row.last_name || ""}`.trim(),
          row.email,
          row.phone,
          row.company?.company_name,
          row?.designation?.designation_name,
          row?.business_type?.name,
          row.is_activated ? "Active" : "Inactive",
          row.last_login_date,
        ]
          .map(csvCell)
          .join(",")
      );
      const csv = [header.map(csvCell).join(","), ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "clients.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      showAlert({ type: "error", message: error.response?.data?.message || "Failed to export data" });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, pageSize, searchText, filters, resetTrigger]);

  useEffect(() => {
    const sorted = sortData(data, sortConfig);
    setFilteredData(sorted);
  }, [data, sortConfig]);

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1); // reset to first page
  };

  const hasActiveQuery = !!searchText || Object.keys(filters).length > 0;

  const handleApplyFilters = ({ search }) => {
    setFilters(search);
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page, newPageSize = pageSize) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleCreate = () => {
    setSelectedClient(null); // Clear selected client for create mode
    setIsModalOpen(true);
  };
  const handleEdit = (id) => {
    const clientToEdit = data.find((client) => client.id === id);
    if (clientToEdit) {
      setSelectedClient(clientToEdit);
      setIsModalOpen(true);
    }
  };

  const handleToggleStatus = async (row) => {
    const confirmed = await openConfirm({
      title: `${row.is_activated ? "Deactivate" : "Activate"} Client`,
      message: `Are you sure you want to ${row.is_activated ? "deactivate" : "activate"} this Client?`,
      confirmText: "Save Changes",
      cancelText: "Cancel",
      icon: <CheckCircle size={24} className="text-success" />,
    });

    if (!confirmed) return;
    try {
      const response = await apiUser.updateStatus({
        id: row.id,
        is_activated: !row.is_activated,
      });

      if (response.data.status) {
        showAlert({
          type: "success",
          message: `User ${row.is_activated ? "deactivated" : "activated"} successfully`,
        });
        fetchUsers();
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      showAlert({
        type: "error",
        message: error.response?.data?.message || "Something went wrong",
      });
    }
  };

  const handleRefresh = () => {
    setSearchText(""); // to reset search text
    setFilters(EMPTY_FILTERS); // to reset advanced filters
    setCurrentPage(1); // to reset page number to 1
    // Bumping resetTrigger clears the search bar and filter drawer, and refetches through
    // the effect once the cleared state is in place (a direct fetch here would send the old one).
    setResetTrigger((prev) => prev + 1);
  };

  const handleModalSuccess = () => {
    fetchUsers(); // Refresh the list
    setSelectedRows([]); // Clear selections
  };

  const columns = [
    {
      key: "id",
      label: "Emp ID",
      type: "number",
      width: "90px",
      fixed: true,
      sortable: true,
      render: (row) => (
        <div className="font-mono text-xs font-medium text-text_primary">
          {`#EMP${String(row.id).padStart(2, "0")}`}
        </div>
      ),
    },
    {
      key: "first_name",
      label: "Employee",
      type: "string",
      width: "220px",
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="relative">
            {row?.profile_picture_url ? (
              <img
                src={row.profile_picture_url}
                alt={`${row.first_name} ${row.last_name}`}
                className="w-9 h-9 rounded-full object-cover border-2 border-surface shadow-sm"
              />
            ) : (
              <Avatar user={row} size={32} rounded={true} />
            )}
            {!row.is_activated && (
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-danger border-2 border-surface rounded-full"></span>
            )}
          </div>
          <div>
            <div className="font-medium text-text_primary">
              {row.first_name} {row.last_name}
            </div>
            <div className="text-xs text-text_muted flex items-center gap-1 mt-0.5">
              <MapPin size={10} />
              {row.city || "N/A"}, {row.country_directory_fid || ""}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "company_name",
      label: "Company",
      type: "string",
      width: "220px",
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="relative">
            {row.company?.company_url ? (
              <img
                src={row.company?.company_url}
                alt={`${row.company?.company_name}`}
                className="w-9 h-9 rounded-full object-cover border-2 border-surface shadow-sm"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-surface_alt flex items-center justify-center text-text_secondary border">
                <Building2 size={18} />
              </div>
            )}
            {!row.is_activated && (
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-danger border-2 border-surface rounded-full"></span>
            )}
          </div>
          <div>
            <div className="font-medium text-text_primary">
              {row.company?.company_name}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "email",
      label: "Contact",
      type: "string",
      width: "220px",
      sortable: true,
      render: (row) => (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-text_secondary">
            <Mail size={12} className="text-text_muted" />
            <span className="truncate max-w-[150px]" title={row.email}>
              {row.email}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-text_secondary">
            <Phone size={12} className="text-text_muted" />
            <span>{row.phone || "-"}</span>
          </div>
        </div>
      ),
    },
    {
      key: "designation_fid",
      label: "Designation",
      type: "string",
      width: "140px",
      sortable: true,
      render: (row) => (
        <div className="font-medium text-text_primary">
          {row?.designation?.designation_name || "-"}
        </div>
      ),
    },
    {
      key: "trade_of_works",
      label: "Trade of Work",
      type: "string",
      width: "180px",
      sortable: false,
      render: (row) => <TradesCell trades={row?.trade_of_works} maxVisible={1} />,
    },
    {
      key: "business_type_fid",
      label: "Business Type",
      type: "string",
      width: "140px",
      sortable: false,
      render: (row) => (
        <div className="font-medium text-text_primary">
          {row?.business_type?.name || "-"}
        </div>
      ),
    },
    {
      key: "assigned_admin_fid",
      label: "Assigned Admin",
      type: "string",
      width: "200px",
      sortable: false,
      render: (row) => {
        const admin = row?.assigned_admin;
        if (!admin) {
          return <span className="text-xs text-text_muted italic">Unassigned</span>;
        }
        return (
          <div className="flex items-center gap-2.5">
            {admin.profile_picture_url ? (
              <img
                src={admin.profile_picture_url}
                alt={`${admin.first_name} ${admin.last_name}`}
                className="w-8 h-8 rounded-full object-cover border-2 border-surface shadow-sm"
              />
            ) : (
              <Avatar user={admin} size={28} rounded={true} />
            )}
            <div className="font-medium text-text_primary text-xs">
              {admin.first_name} {admin.last_name}
            </div>
          </div>
        );
      },
    },
    {
      key: "is_approved",
      label: "Approval",
      type: "boolean",
      width: "130px",
      sortable: true,
      render: (row) => (
        <div className="space-y-1.5">
          {row.is_approved ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-success_bg text-success rounded-lg text-xs font-medium border border-success_border">
              <CheckCircle size={12} />
              Approved
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-warning_bg text-warning rounded-lg text-xs font-medium border border-warning_border">
              <XCircle size={12} />
              Pending
            </span>
          )}

          {row.account_locked_until && (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-danger_bg text-danger rounded-lg text-xs font-medium border border-danger_border">
              <Lock size={10} />
              Locked
            </span>
          )}
        </div>
      ),
    },
    {
      key: "is_login_verified",
      label: "Email Verified",
      type: "boolean",
      width: "100px",
      sortable: false,
      render: (row) => (
        <div className="flex flex-col gap-1.5 items-start">
          {row.is_login_verified ? (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-medium tracking-wide
          bg-success_bg text-success border border-success_border"
            >
              <CheckCircle size={13} />
              Verified
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-medium tracking-wide
          bg-warning_bg text-warning border border-warning_border"
            >
              <Clock size={13} />
              Pending
            </span>
          )}
        </div>
      ),
    },
    {
      key: "is_activated",
      label: "Status",
      type: "boolean",
      width: "130px",
      sortable: true,
      render: (row) => (
        <div className="space-y-1.5">
          {row.is_activated ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-success_bg text-success rounded  text-xs font-medium border border-success_border">
              <UserCheck size={12} />
              Active
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-danger_bg text-danger rounded  text-xs font-medium border border-danger_border">
              <UserX size={12} />
              Inactive
            </span>
          )}
        </div>
      ),
    },
    {
      key: "last_login_date",
      label: "Last Login",
      type: "date",
      width: "160px",
      sortable: true,
      render: (row) => (
        <div>
          <div className="flex items-center gap-1.5 text-xs text-text_primary">
            <Calendar size={12} className="text-text_muted" />

            {row.last_login_date ? (
              <LocalDateTime utcTime={row.last_login_date} />
            ) : (
              <span className="text-text_muted italic">Never Logged In</span>
            )}
          </div>

          <div className="flex items-center gap-1.5 text-xs text-text_muted mt-1">
            <Clock size={10} className="text-text_muted" />
            Logins: {row.login_count ?? 0}
          </div>
        </div>
      ),
    },
    {
      key: "last_active",
      label: "Last Online",
      type: "date",
      width: "160px",
      sortable: true,
      render: (row) => (
        <div>
          <div className="flex items-center gap-1.5 text-xs text-text_primary">
            <Calendar size={12} className="text-text_muted" />

            {row.last_active ? (
              <LocalDateTime utcTime={row.last_active} />
            ) : (
              <span className="text-text_muted italic">Not Online Yet</span>
            )}
          </div>
        </div>
      ),
    },
  ];

  // Custom action buttons for each row
  const renderRowActions = (row) => (
    <div className="flex items-center gap-1.5">
      {/* Edit */}
      <button
        className="p-1.5 rounded-lg bg-info_bg text-info hover:bg-info_bg transition-all duration-200"
        onClick={(e) => {
          e.stopPropagation();
          handleEdit(row.id);
        }}
        title="Edit Client"
      >
        <Edit size={16} />
      </button>

      {/* Delete */}
      <button
        className="p-1.5 rounded-lg bg-danger_bg text-danger hover:bg-danger_bg transition-all duration-200"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteSelected([row.id]);
        }}
        title="Delete Client"
      >
        <Trash2 size={16} />
      </button>

      {/* Activate / Deactivate */}
      <button
        className={`p-1.5 rounded-lg transition-all duration-200 ${
          row.is_activated
            ? "bg-danger_bg text-danger hover:bg-danger_bg"
            : "bg-success_bg text-success hover:bg-success_bg"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          handleToggleStatus(row);
        }}
        title={row.is_activated ? "Deactivate User" : "Activate User"}
      >
        {row.is_activated ? <UserX size={16} /> : <UserCheck size={16} />}
      </button>
    </div>
  );

  return (
    <div className="h-full flex flex-col overflow-hidden bg-surface_alt">
      <div className="flex flex-col h-full gap-4">
        <TableToolBar
          onSearch={handleSearch}
          onRefresh={handleRefresh}
          onCreate={handleCreate}
          onDeleteSelected={() => handleDeleteSelected(selectedRows)}
          selectedCount={selectedRows.length}
          resetSearchTrigger={resetTrigger}
          resultCount={totalCount}
          sortOptions={SORT_OPTIONS}
          sortValue={sortValue}
          onSortChange={handleSortChange}
          onClearSelection={handleClearSelection}
          onExport={handleExport}
          exporting={exporting}
          advanceFilter={
            <GlobalAdvanceFilter
              fields={clientFilterFields}
              dependenciesLoader={loadClientFilterDependencies}
              onApply={handleApplyFilters}
              resetKey={resetTrigger}
            />
          }
        />

        {data?.length === 0 && !loading ? (
          <div className="flex-1 flex items-center justify-center border rounded-lg border-border bg-surface">
            <NoDataState
              icon="users"
              title="No Client Found"
              message={
                hasActiveQuery
                  ? "No clients match your search or filters."
                  : "There is no client available in your company!"
              }
              showRefresh={false}
              showBack={false}
              showCreate={true}
              createText="Create New Client"
              onCreate={handleCreate}
              size="medium"
            />
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-hidden border rounded-lg border-border">
              <TableGrid
                data={filteredData}
                totalCount={totalCount}
                loading={loading}
                currentPage={currentPage}
                columns={columns}
                sortConfig={sortConfig}
                onSort={handleSort}
                onSelectionChange={setSelectedRows}
                pageSize={pageSize}
                selectable={true}
                renderActions={renderRowActions}
                resetSelectionTrigger={resetSelectionTrigger}
              />
            </div>

            <Pagination
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />

            {confirmData.isOpen && (
              <ConfirmDialogue
                confirmData={confirmData}
                handleConfirmClose={handleConfirmClose}
              />
            )}
          </>
        )}
      </div>

      <EntityFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        id={selectedClient?.id}
        title="Client"
        fetchById={apiUser.getById}
        createFn={apiUser.create}
        updateFn={apiUser.update}
        dependenciesLoader={loadClientDependencies}
        fields={clientFieldsConfig}
        validationSchema={clientValidation}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default Clients;
